import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import AdminService from '../../services/adminService';

/**
 * Packages Manager Component
 *
 * Manages service packages for immigration pathways:
 * - Create/edit/delete packages with pricing
 * - Configure package perks and benefits
 * - Multi-language support for package descriptions
 * - Tier-based package system (Basic, Premium, VIP)
 */
const PackagesManager = ({ pathway, onUpdate, onCancel, serviceCurrency = 'LKR' }) => {
  const { t } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddPackage, setShowAddPackage] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const normalizedServiceCurrency = (serviceCurrency || 'LKR').toUpperCase();

  useEffect(() => {
    if (pathway?.id) {
      loadPackages();
    }
  }, [pathway]);

  const loadPackages = async () => {
    try {
      setLoading(true);
      const data = await AdminService.getPackages(pathway.id);
      setPackages(data || []);
    } catch (error) {
      console.error('Error loading packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPackage = () => {
    const defaultCurrency = (pathway?.service_currency || pathway?.currency || normalizedServiceCurrency).toUpperCase();
    setEditingPackage({
      package_key: '',
      package_name: '',
      description: '',
      price_range_min: 0,
      price_range_max: 0,
      currency: defaultCurrency,
      is_active: true,
      sort_order: packages.length + 1,
      perks: []
    });
    setShowAddPackage(true);
  };

  const handleEditPackage = (pkg) => {
    const defaultCurrency = (pathway?.service_currency || pathway?.currency || normalizedServiceCurrency).toUpperCase();
    setEditingPackage({
      ...pkg,
      currency: (pkg.currency || defaultCurrency).toUpperCase(),
      perks: pkg.package_perks || []
    });
    setShowAddPackage(true);
  };

  const handleSavePackage = async (packageData) => {
    try {
      setLoading(true);
      const normalizedPackageData = {
        ...packageData,
        currency: (packageData.currency || normalizedServiceCurrency).toUpperCase()
      };

      if (editingPackage.id) {
        await AdminService.updatePackage(editingPackage.id, normalizedPackageData);

        // Update perks
        const existingPerks = editingPackage.perks || [];
        const newPerks = normalizedPackageData.perks || [];

        // Delete removed perks
        for (const existingPerk of existingPerks) {
          if (!newPerks.find(p => p.id === existingPerk.id)) {
            await AdminService.deletePackagePerk(existingPerk.id);
          }
        }

        // Add/update perks
        for (const perk of newPerks) {
          if (perk.id) {
            await AdminService.updatePackagePerk(perk.id, perk);
          } else {
            await AdminService.createPackagePerk({
              ...perk,
              package_id: editingPackage.id
            });
          }
        }
      } else {
        // Create new package
        const newPackage = await AdminService.createPackage({
          ...normalizedPackageData,
          pathway_id: pathway.id
        });

        // Add perks
        for (const perk of normalizedPackageData.perks || []) {
          await AdminService.createPackagePerk({
            ...perk,
            package_id: newPackage.id
          });
        }
      }

      setShowAddPackage(false);
      setEditingPackage(null);
      await loadPackages();
      onUpdate && onUpdate();
    } catch (error) {
      console.error('Error saving package:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) {
      return;
    }

    try {
      await AdminService.deletePackage(packageId);
      await loadPackages();
      onUpdate && onUpdate();
    } catch (error) {
      console.error('Error deleting package:', error);
    }
  };

  const getPackageTypeLabel = (packageKey) => {
    const labels = {
      basic_package: 'Basic Package',
      premium_package: 'Premium Package',
      vip_package: 'VIP Package',
      standard: 'Standard',
      economy: 'Economy',
      business: 'Business'
    };
    return labels[packageKey] || packageKey;
  };

  if (loading && packages.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-white">
            Service Packages
          </h3>
          <p className="text-sm text-gray-400">
            Configure service packages with perks and benefits
          </p>
        </div>
        <button
          onClick={handleAddPackage}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          Add Package
        </button>
      </div>

      {/* Enable Packages Toggle */}
      <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-4">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={pathway?.packages_enabled || false}
            onChange={async (e) => {
              if (onUpdate) {
                await onUpdate({ packages_enabled: e.target.checked });
              }
            }}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 rounded bg-gray-700"
          />
          <span className="ml-2 text-sm font-medium text-gray-300">
            Enable packages for this pathway
          </span>
        </label>
        <p className="mt-1 text-xs text-gray-400">
          When enabled, users can select from different service packages with varying perks and pricing
        </p>
      </div>

      {/* Packages List */}
      {packages.length === 0 ? (
        <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-600 rounded-lg">
          No packages configured yet. Click "Add Package" to get started.
        </div>
      ) : (
        <div className="space-y-4">
          {packages.map((pkg) => (
            <div key={pkg.id} className="p-4 border border-gray-700 rounded-lg bg-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <span className="px-2 py-1 bg-purple-900/30 text-purple-400 border border-purple-800 text-xs font-medium rounded">
                      {getPackageTypeLabel(pkg.package_key)}
                    </span>
                    <span className="font-medium text-white">
                      {pkg.package_name}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-400">{pkg.description}</p>
                  <div className="mt-2 text-sm text-gray-600">
                    {(() => {
                      const minPrice = Number(pkg.price_range_min || 0);
                      const maxPrice = Number(pkg.price_range_max || 0);
                      const currencyLabel = (pkg.currency || normalizedServiceCurrency).toUpperCase();
                      const formattedMin = minPrice.toLocaleString();
                      const formattedMax = maxPrice.toLocaleString();
                      const priceText = maxPrice && maxPrice !== minPrice
                        ? `${currencyLabel} ${formattedMin} - ${formattedMax}`
                        : `${currencyLabel} ${formattedMin}`;
                      return (
                        <span className="font-semibold text-green-400">
                          {priceText}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {pkg.package_perks?.slice(0, 3).map((perk) => (
                      <span key={perk.id} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-300">
                        {perk.perk_description}
                      </span>
                    ))}
                    {pkg.package_perks?.length > 3 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-300">
                        +{pkg.package_perks.length - 3} more
                      </span>
                    )}
                  </div>
                  {!pkg.is_active && (
                    <div className="mt-1">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-300">
                        Inactive
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEditPackage(pkg)}
                    className="p-2 text-blue-400 hover:bg-blue-900/30 rounded-lg"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeletePackage(pkg.id)}
                    className="p-2 text-red-400 hover:bg-red-900/30 rounded-lg"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Package Modal */}
      {showAddPackage && (
        <PackageForm
          package={editingPackage}
          serviceCurrency={serviceCurrency}
          onSave={handleSavePackage}
          onCancel={() => {
            setShowAddPackage(false);
            setEditingPackage(null);
          }}
        />
      )}
    </div>
  );
};

/**
 * Package Form Component
 */
const PackageForm = ({ package: pkg, onSave, onCancel, serviceCurrency = 'LKR' }) => {
  const normalizedServiceCurrency = (serviceCurrency || 'LKR').toUpperCase();
  const [formData, setFormData] = useState({
    package_key: pkg?.package_key || 'basic_package',
    package_name: pkg?.package_name || '',
    description: pkg?.description || '',
    price_range_min: pkg?.price_range_min || 0,
    price_range_max: pkg?.price_range_max || 0,
    currency: pkg?.currency ? pkg.currency.toUpperCase() : normalizedServiceCurrency,
    is_active: pkg?.is_active !== undefined ? pkg.is_active : true,
    sort_order: pkg?.sort_order || 1,
    perks: pkg?.perks || []
  });

  const [newPerk, setNewPerk] = useState('');

  const packageTypes = [
    { value: 'basic_package', label: 'Basic Package' },
    { value: 'premium_package', label: 'Premium Package' },
    { value: 'vip_package', label: 'VIP Package' },
    { value: 'standard', label: 'Standard' },
    { value: 'economy', label: 'Economy' },
    { value: 'business', label: 'Business' }
  ];

  const currencies = [
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'CAD', label: 'CAD - Canadian Dollar' },
    { value: 'AUD', label: 'AUD - Australian Dollar' },
    { value: 'LKR', label: 'LKR - Sri Lankan Rupee' }
  ];

  const addPerk = () => {
    if (newPerk.trim()) {
      setFormData(prev => ({
        ...prev,
        perks: [...prev.perks, {
          id: Date.now().toString(), // temporary ID
          perk_key: newPerk.toLowerCase().replace(/\s+/g, '_'),
          perk_description: newPerk.trim(),
          is_included: true,
          sort_order: prev.perks.length + 1
        }]
      }));
      setNewPerk('');
    }
  };

  const removePerk = (index) => {
    setFormData(prev => ({
      ...prev,
      perks: prev.perks.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-white">
              {pkg?.id ? 'Edit Package' : 'Add Package'}
            </h3>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Package Type
                  </label>
                  <select
                    value={formData.package_key}
                    onChange={(e) => setFormData(prev => ({ ...prev, package_key: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {packageTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Currency
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {currencies.map(currency => (
                      <option key={currency.value} value={currency.value}>{currency.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Package Name
                </label>
                <input
                  type="text"
                  value={formData.package_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, package_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
                  placeholder="e.g., Premium Student Package"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
                  placeholder="Describe what's included in this package"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Min Price
                  </label>
                  <input
                    type="number"
                    value={formData.price_range_min}
                    onChange={(e) => setFormData(prev => ({ ...prev, price_range_min: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Max Price
                  </label>
                  <input
                    type="number"
                    value={formData.price_range_max}
                    onChange={(e) => setFormData(prev => ({ ...prev, price_range_max: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Package Perks */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Package Perks & Benefits
                </label>

                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newPerk}
                    onChange={(e) => setNewPerk(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPerk())}
                    className="flex-1 px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
                    placeholder="e.g., Airport pickup, Accommodation assistance"
                  />
                  <button
                    type="button"
                    onClick={addPerk}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Add
                  </button>
                </div>

                <div className="space-y-2">
                  {formData.perks.map((perk, index) => (
                    <div key={perk.id || index} className="flex items-center justify-between p-2 bg-gray-700/50 rounded border border-gray-600">
                      <span className="text-sm text-gray-300">{perk.perk_description}</span>
                      <button
                        type="button"
                        onClick={() => removePerk(index)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 rounded bg-gray-700"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-300">
                    Active Package
                  </span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-700">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Package
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PackagesManager;
